import React, { useEffect } from 'react';

const sections = [
  {
    title: '1. Responsable del tratamiento',
    body: [
      'El responsable del tratamiento es Ancora Automations, empresa dedicada a automatizaciones con inteligencia artificial para atención al cliente, reservas, ventas, marketing e integraciones de negocio.',
      'Contacto de privacidad: automatiza@ancoraautomations.es · Teléfono: +34 614 046 352',
    ],
  },
  {
    title: '2. Aplicación y servicios cubiertos',
    body: [
      'Esta política cubre la web de Ancora Automations y las aplicaciones o integraciones que Ancora Automations conecta con servicios de Meta, incluyendo WhatsApp Business Platform, Instagram, páginas de Facebook y herramientas relacionadas para atender mensajes, gestionar solicitudes, coordinar reservas, dar soporte y operar automatizaciones contratadas por clientes.',
    ],
  },
  {
    title: '3. Datos personales que podemos tratar',
    list: [
      'Datos de contacto: nombre, empresa, correo electrónico, teléfono y canal preferido de comunicación.',
      'Mensajes y contenido enviado voluntariamente por WhatsApp, Instagram, formularios web, email u otros canales conectados.',
      'Datos necesarios para prestar el servicio solicitado: solicitudes de información, reservas, incidencias, preferencias comunicadas por el usuario y estado de conversaciones comerciales o de soporte.',
      'Identificadores técnicos o de canal facilitados por Meta o por el propio canal, como número de teléfono de WhatsApp, identificador de usuario/página, nombre de perfil público, fecha/hora del mensaje y metadatos necesarios para entregar y responder comunicaciones.',
      'Datos técnicos básicos de uso de la web, como dirección IP, navegador, páginas visitadas y eventos de analítica cuando proceda.',
    ],
  },
  {
    title: '4. Finalidades del tratamiento',
    list: [
      'Responder solicitudes recibidas por la web, WhatsApp, Instagram, Facebook, email u otros canales.',
      'Prestar servicios de automatización, atención al cliente, gestión de reservas, seguimiento comercial, soporte e integraciones contratadas.',
      'Mantener el historial necesario para continuar conversaciones, evitar duplicados y mejorar la calidad del servicio.',
      'Enviar comunicaciones relacionadas con una solicitud, contrato, servicio activo o consentimiento otorgado.',
      'Proteger la seguridad de la aplicación, prevenir abuso, depurar incidencias técnicas y cumplir obligaciones legales.',
    ],
  },
  {
    title: '5. Base jurídica',
    body: [
      'Tratamos los datos cuando existe consentimiento del usuario, cuando el tratamiento es necesario para gestionar una solicitud o contrato, cuando existe interés legítimo en mantener la seguridad y continuidad del servicio, o cuando debemos cumplir obligaciones legales aplicables.',
    ],
  },
  {
    title: '6. Compartición de datos y encargados',
    body: [
      'Ancora Automations no vende datos personales. Podemos utilizar proveedores tecnológicos necesarios para prestar el servicio, como Meta/WhatsApp/Instagram, proveedores de hosting, correo electrónico, agenda, CRM, analítica, herramientas de automatización o servicios de inteligencia artificial. Estos proveedores actúan según sus propias condiciones y/o como encargados del tratamiento cuando corresponde, y solo se usan para las finalidades descritas.',
    ],
  },
  {
    title: '7. Conservación',
    body: [
      'Conservamos los datos durante el tiempo necesario para gestionar la relación, prestar el servicio solicitado, resolver incidencias, cumplir obligaciones legales y mantener registros razonables de seguridad. Cuando los datos dejan de ser necesarios, se eliminan o anonimizan de forma segura.',
    ],
  },
  {
    title: '8. Derechos y eliminación de datos',
    body: [
      'Puedes solicitar acceso, rectificación, supresión, oposición, limitación del tratamiento y portabilidad escribiendo a automatiza@ancoraautomations.es. Para solicitar la eliminación de datos, escribe con el asunto “Solicitud de eliminación de datos” o consulta https://www.ancoraautomations.es/eliminacion-datos.',
    ],
  },
  {
    title: '9. Cambios en esta política',
    body: [
      'Podemos actualizar esta política para reflejar cambios legales, técnicos o de nuestros servicios. La versión vigente estará siempre disponible en la URL pública de política de privacidad.',
    ],
  },
];

export const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const description = 'Política de privacidad de Ancora Automations y de sus aplicaciones conectadas a Meta, WhatsApp e Instagram.';
    const canonicalUrl = 'https://www.ancoraautomations.es/privacidad';
    document.title = 'Política de privacidad | Ancora Automations';

    let metaDescription = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = metaDescription?.content;
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = description;

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const previousCanonical = canonical?.href;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    const openGraphTags = [
      { property: 'fb:app_id', content: '1864910794392369' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:title', content: 'Política de privacidad | Ancora Automations' },
      { property: 'og:description', content: description },
      { property: 'og:image', content: 'https://www.ancoraautomations.es/og-image.png' },
      { property: 'og:image:secure_url', content: 'https://www.ancoraautomations.es/og-image.png' },
      { property: 'og:image:type', content: 'image/png' },
      { property: 'og:image:width', content: '1536' },
      { property: 'og:image:height', content: '1024' },
      { property: 'og:image:alt', content: 'Ancora Automations - automatizaciones con IA para atención, ventas y marketing' },
      { property: 'og:site_name', content: 'Ancora Automations' },
    ];

    const previousOpenGraphTags = openGraphTags.map(({ property, content }) => {
      let tag = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
      const created = !tag;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      const previousContent = tag.content;
      tag.content = content;
      return { tag, created, previousContent };
    });

    return () => {
      document.title = previousTitle;
      if (metaDescription && previousDescription !== undefined) {
        metaDescription.content = previousDescription;
      }
      if (canonical && previousCanonical) {
        canonical.href = previousCanonical;
      }
      previousOpenGraphTags.forEach(({ tag, created, previousContent }) => {
        if (created) {
          tag.remove();
        } else {
          tag.content = previousContent;
        }
      });
    };
  }, []);

  return (
    <div className="marketing-shell legal-page pt-32 md:pt-36 pb-24 bg-[#f6f8f7]">
      <div className="container mx-auto px-4 max-w-4xl">
        <p className="text-sm font-bold text-[#087064] uppercase tracking-[0.2em] mb-4">Ancora Automations</p>
        <h1 className="legal-page__title text-4xl md:text-5xl text-[#10232d] mb-4">Política de Privacidad</h1>
        <p className="text-[#536970] mb-6">Última actualización: 18 de junio de 2026</p>
        <p className="text-[#536970] mb-12 rounded-2xl border border-[#b9d8d0] bg-[#dceee9] p-5">
          Esta página describe cómo Ancora Automations trata los datos personales en su web y en sus aplicaciones conectadas a Meta, WhatsApp e Instagram. La página pública para revisiones de Meta es{' '}
          <a href="/privacidad" className="break-all font-bold text-[#087064] underline">
            https://www.ancoraautomations.es/privacidad
          </a>
          .
        </p>
        <div className="prose prose-slate max-w-none">
          {sections.map((section) => (
            <section key={section.title} className="py-8 border-t border-[#d7e4e2]">
              <h2 className="text-xl font-bold text-[#10232d] mb-4">{section.title}</h2>
              {section.body?.map((paragraph) => (
                <p key={paragraph} className="text-[#536970] mb-3">{paragraph}</p>
              ))}
              {section.list && (
                <ul className="list-disc pl-6 mb-4 space-y-1 text-[#536970]">
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};
